import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { postContact } from "@/lib/api";

const INTERESTS = [
  { id: "general", label: "General inquiry" },
  { id: "meat-app", label: "LiveCuts partnership" },
  { id: "sheep-invest", label: "Flockfolio investing" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    interest: "general",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Message sent.", {
        description: "We’ll reply within one working day.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: "",
        interest: "general",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        "Please check your details and try again.";
      toast.error("Couldn’t send message", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="border-b border-bone-300">
        <div className="container-x py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline">/ Contact</div>
            <h1
              data-testid="contact-heading"
              className="mt-6 font-display font-black text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-tighter text-ink-olive"
            >
              Let’s talk.
              <br />
              <span className="font-serif italic font-light">
                Say hello, ask anything.
              </span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-ink-soft">
              Whether you want to partner with LiveCuts,
              or explore Flockfolio investing — we read every note.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-6">
            <ContactBlock
              icon={Mail}
              label="Email"
              value="hr@thiruhanarayanoinnovations.com"
              testid="contact-email"
            />
            <ContactBlock
              icon={Phone}
              label="Phone"
              value="+91 78422 55558"
              testid="contact-phone"
            />
            <ContactBlock
              icon={MapPin}
              label="Studio"
              value={"3-7-400/DB/2, Sanjeevani Hills Rd 2\nHyderguda, Hyderabad — 500048\nTelangana, India"}
              testid="contact-address"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-bone-300 bg-bone-50">
        <div className="container-x py-24 sm:py-32">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8"
          >
            <div className="lg:col-span-4">
              <div className="overline">/ Message</div>
              <h2 className="mt-4 font-serif italic font-light text-3xl sm:text-4xl text-ink-olive">
                Tell us the shape of your problem.
              </h2>
              <div className="mt-8">
                <label className="mono-label text-ink-soft">/ I’m interested in</label>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {INTERESTS.map((int) => (
                    <button
                      type="button"
                      key={int.id}
                      onClick={() => update("interest", int.id)}
                      data-testid={`contact-interest-${int.id}`}
                      className={`px-3 py-3 font-sans text-sm border text-left transition-colors ${
                        form.interest === int.id
                          ? "bg-ink-olive text-bone border-ink-olive"
                          : "bg-white text-ink-olive border-bone-300 hover:border-ink-olive"
                      }`}
                    >
                      {int.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 border border-bone-300 bg-white p-8 sm:p-10 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field
                  label="Full name"
                  testid="contact-input-name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  testid="contact-input-email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  required
                />
                <Field
                  label="Phone"
                  testid="contact-input-phone"
                  value={form.phone}
                  onChange={(v) => update("phone", v)}
                />
                <Field
                  label="Company"
                  testid="contact-input-company"
                  value={form.company}
                  onChange={(v) => update("company", v)}
                />
              </div>
              <Field
                label="Subject"
                testid="contact-input-subject"
                value={form.subject}
                onChange={(v) => update("subject", v)}
                required
              />
              <div>
                <label className="mono-label text-ink-soft">/ Message</label>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  data-testid="contact-input-message"
                  required
                  className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-ink-olive focus:outline-none"
                  placeholder="What are you trying to build or partner on?"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"}{" "}
                <ArrowUpRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function ContactBlock({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="border border-bone-300 bg-white p-6 flex items-start gap-4"
      data-testid={testid}
    >
      <span className="inline-flex items-center justify-center w-10 h-10 bg-bone-100 border border-bone-300 text-ink-olive shrink-0">
        <Icon size={18} />
      </span>
      <div>
        <div className="mono-label text-terracotta">/ {label}</div>
        <div className="mt-1 font-display text-lg text-ink-olive whitespace-pre-line">
          {value}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", ...rest }) {
  return (
    <div>
      <label className="mono-label text-ink-soft">/ {label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-ink-olive focus:outline-none"
        {...rest}
      />
    </div>
  );
}
